import { useEffect, useRef, useState } from 'react';
import { type MessageDto } from '@shared';
import { apiGet, apiPost, ApiError } from '../lib/api';

interface MessageThreadProps {
  requestId: string;
  // 'staff' enables the Internal note toggle; 'client' hides it.
  perspective: 'staff' | 'client';
  // currentUserId is used to colour the caller's own bubbles.
  currentUserId: string;
}

// Threaded chat for one request. Lightweight polling could be added later;
// for v1 we fetch on mount + after each post + after mark-read.
export function MessageThread({ requestId, perspective, currentUserId }: MessageThreadProps) {
  const [messages, setMessages] = useState<MessageDto[] | null>(null);
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  async function load() {
    try {
      const r = await apiGet<{ messages: MessageDto[] }>(`/api/messages?request_id=${encodeURIComponent(requestId)}`);
      setMessages(r.messages);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Could not load messages.');
    }
  }

  useEffect(() => {
    void load();
    // Mark thread read on mount so unread counts elsewhere can decay.
    apiPost('/api/messages/mark-read', { request_id: requestId }).catch(() => { /* best-effort */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  async function handleSend() {
    if (!body.trim() || posting) return;
    setPosting(true);
    setError(null);
    try {
      await apiPost<{ message: MessageDto }>('/api/messages', {
        request_id: requestId,
        body: body.trim(),
        internal: perspective === 'staff' ? internal : false,
      });
      setBody('');
      setInternal(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Could not send.');
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="msg-thread">
      <div className="msg-list" ref={listRef}>
        {messages === null && <div className="msg-empty">Loading…</div>}
        {messages !== null && messages.length === 0 && (
          <div className="msg-empty">
            No messages yet. {perspective === 'client'
              ? 'Drop a note to your engagement lead below.'
              : 'Reach out to the client below.'}
          </div>
        )}
        {(messages ?? []).map((m) => {
          const isMine = m.author_id === currentUserId;
          const isInternal = m.internal;
          return (
            <div
              key={m.id}
              className={`msg ${isMine ? 'msg-mine' : 'msg-theirs'} ${isInternal ? 'msg-internal' : ''}`}
            >
              <div className="msg-meta">
                <strong>{m.author_name}</strong>
                <span className="msg-role">{m.author_kind}</span>
                {isInternal && <span className="msg-internal-tag">Internal</span>}
                <span className="msg-time">{m.created_at.slice(0, 16).replace('T', ' ')}</span>
              </div>
              <div className="msg-body">{m.body}</div>
            </div>
          );
        })}
      </div>

      <div className="msg-composer">
        {perspective === 'staff' && (
          <label className="msg-internal-toggle">
            <input
              type="checkbox"
              checked={internal}
              disabled={posting}
              onChange={(e) => setInternal(e.target.checked)}
            />
            Internal note (client cannot see)
          </label>
        )}
        <textarea
          rows={3}
          className="prop-textarea"
          placeholder={perspective === 'staff' && internal
            ? 'Visible to staff only.'
            : 'Write a message…'}
          value={body}
          disabled={posting}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              void handleSend();
            }
          }}
        />
        {error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 4 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--fg-mute)' }}>⌘/Ctrl + Enter to send</span>
          <button type="button" className="btn" onClick={handleSend} disabled={!body.trim() || posting}>
            {posting ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
